import { Link } from "react-router-dom";
import { CalendarCheck, MessageSquareText, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { BRAND, SPECIALS, LOGO } from "@/data/site";
import { Reveal, ChapterLabel } from "@/components/Reveal";

export default function Specials() {
  return (
    <section className="relative py-28 sm:py-36 bg-[#060f26] border-y border-cyan-400/10 overflow-hidden grain-overlay" data-testid="specials-section">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-cyan-400/5 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end gap-10 lg:gap-16">
          <Reveal className="flex-1">
            <ChapterLabel number="00" text="Limited-Time Offer" />
            <h2 className="font-display font-black italic uppercase tracking-tighter leading-[0.9] text-4xl sm:text-6xl lg:text-7xl" data-testid="specials-title">
              Stop Paying <span className="text-cyan-400">$200</span> For A Single Wash!
            </h2>
            <p className="mt-6 max-w-xl text-slate-300 leading-relaxed">
              Save money with our exclusive seasonal pressure washing plans. Sign up today, choose your cleaning dates, and keep your property looking beautiful all season long.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <img
              src={LOGO}
              alt="Your Surface My Purpose — Power Washing & Surface Cleaner, sponsored by American Laborers Inc"
              className="w-36 h-36 sm:w-44 sm:h-44 rounded-2xl border border-cyan-400/30 object-cover shadow-[0_0_60px_rgba(0,240,255,0.15)]"
              data-testid="specials-logo"
            />
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
          {SPECIALS.map((plan, i) => (
            <Reveal key={plan.id} delay={i * 0.12}>
              <div
                data-testid={`special-card-${plan.id}`}
                className={`relative h-full rounded-3xl p-8 sm:p-10 flex flex-col ${
                  plan.highlight
                    ? "bg-[#0f2048] border-2 border-cyan-400 shadow-[0_0_60px_rgba(0,240,255,0.2)]"
                    : "bg-[#0b1838] border border-cyan-400/15"
                }`}
              >
                {plan.highlight && (
                  <span className="absolute -top-4 left-8 bg-cyan-400 text-[#030712] font-mono-brand text-xs font-bold tracking-[0.25em] uppercase px-4 py-1.5 rounded-full" data-testid="best-value-badge">
                    Best Value
                  </span>
                )}
                <p className="font-mono-brand text-xs tracking-[0.3em] uppercase text-cyan-400 font-bold">{plan.label}</p>
                <h3 className="mt-3 font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl">{plan.washes} Washes</h3>

                <div className="mt-6 flex items-end gap-3">
                  <span className={`font-display font-black italic leading-none text-6xl sm:text-7xl ${plan.highlight ? "text-cyan-400" : "text-white"}`} data-testid={`special-price-${plan.id}`}>
                    ${plan.perWash}
                  </span>
                  <span className="pb-2 text-slate-400 text-sm font-semibold uppercase tracking-widest">Per Wash</span>
                </div>
                <p className="mt-2 text-sm text-slate-500">
                  <s>${plan.regular} per wash</s> — <span className="text-slate-300 font-semibold">Total: ${plan.total}</span>
                </p>

                <ul className="mt-6 flex flex-col gap-3 text-sm text-slate-300">
                  <li className="flex items-center gap-3"><CalendarCheck className="w-4 h-4 text-cyan-400 shrink-0" /> Choose your own cleaning dates</li>
                  <li className="flex items-center gap-3"><Sparkles className="w-4 h-4 text-cyan-400 shrink-0" /> Beautiful property all season long</li>
                  <li className="flex items-center gap-3"><ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0" /> Lock in savings before the rush</li>
                </ul>

                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href={`sms:${BRAND.phoneRaw}?&body=Hi! I'd like to sign up for the ${plan.washes}-wash seasonal plan ($${plan.total}).`}
                    data-testid={`special-claim-${plan.id}`}
                    className={`flex items-center gap-2 font-bold px-6 py-3.5 rounded-full transition-transform hover:scale-105 ${
                      plan.highlight ? "bg-cyan-400 text-[#030712]" : "border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10"
                    }`}
                  >
                    <MessageSquareText className="w-4 h-4" /> Claim This Deal
                  </a>
                  <Link
                    to="/contact"
                    data-testid={`special-estimate-${plan.id}`}
                    className="group flex items-center gap-2 text-slate-300 font-semibold hover:text-cyan-400 transition-colors px-2 py-3.5"
                  >
                    Ask First <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
